import { Icon, Image, Tooltip, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { constants } from '@hero/configs';
import { PRODUCT_TYPES } from '@hero/constants';
import { useCreatePriceLabel, useGetProductSkuImages } from '@hero/hooks';
import { getFormattedPrice } from '@hero/utils';

import { PriceBreakdownDropdown } from '../../PriceBreakdownDropdown';

import * as S from './style';

export default function VerticalListItem({
  product,
  onClick,
  onRemoveItem,
  isSelected,
  isAvailableSku = true,
  isAllSkusAvailable = true
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isProductCodeHovered, setIsProductCodeHovered] = useState(false);

  const {
    priceLabel: productPriceLabel,
    maxSkuPrice,
    minSkuPrice,
    isCAActMustBeApplied
  } = useCreatePriceLabel(product);
  const formatedPriceLabel = getFormattedPrice({
    min: minSkuPrice,
    max: maxSkuPrice
  });
  const priceLabel =
    minSkuPrice && maxSkuPrice ? formatedPriceLabel : productPriceLabel?.priceLabel;

  const isSomeSkuAvailable = !product?.orderable && isAvailableSku;
  const isAllSkuAvailable = !!product?.orderable && isAllSkusAvailable;

  const images = useGetProductSkuImages({ product });

  return (
    <S.VerticalListItem isSelected={isSelected} isAllSkuAvailable={isAllSkuAvailable}>
      <Image
        onClick={() => !!onClick && onClick(product)}
        src={images?.[0]?.url || theme.images.noImage}
        alt={product?.name}
        width="52px"
        height="fit-content"
      />
      <S.InfoWrapper onClick={() => !!onClick && onClick(product)}>
        {constants?.brands?.data[product?.brand?.id]?.logo && (
          <S.ProductInfoImage>
            <Image
              src={product?.meta?.logoUrl || constants.brands.data[product?.brand.id].logo}
              alt={product?.name}
            />
          </S.ProductInfoImage>
        )}
        {product?.meta?.productType === PRODUCT_TYPES.GIFT_CARD &&
          product?.meta?.isInternational && (
            <S.EGiftCardInternationalTooltipWrapper>
              <Tooltip
                content={t('eGiftCardInternationalText')}
                size=""
                tooltipConfig={{ placement: 'top' }}
              >
                <Icon name="globe" />
              </Tooltip>
            </S.EGiftCardInternationalTooltipWrapper>
          )}
        {product?.brand?.name && <Typography variant="caption1">{product?.brand?.name}</Typography>}
        <Typography variant="caption1" emphasis="weaker" as="div" style={{ display: 'flex' }}>
          {!!product?.merchantProductId && <>{t('code')}:&nbsp;</>}
          <Typography
            variant="caption1Bold"
            emphasis="weaker"
            onMouseEnter={() => setIsProductCodeHovered(true)}
            onMouseLeave={() => setIsProductCodeHovered(false)}
            style={isProductCodeHovered ? { zIndex: '99999' } : {}}
          >
            {product?.merchantProductId}
          </Typography>
        </Typography>
        <S.Name>
          <div dangerouslySetInnerHTML={{ __html: product?.name }} />
        </S.Name>
        <S.Price>{priceLabel}</S.Price>
        {isCAActMustBeApplied && <PriceBreakdownDropdown product={product} />}
      </S.InfoWrapper>
      {onRemoveItem && (
        <S.CloseButton variant="outlined" onClick={onRemoveItem} color="secondary">
          <Icon name="close" />
        </S.CloseButton>
      )}
      {!isAllSkuAvailable ? (
        <S.ProductStockInfoOverlay>
          <S.ProductStockInfo>
            <Tooltip
              content={
                <Typography variant="body2" color="bg">
                  <Trans i18nKey="allVariantsUnavailable" />
                </Typography>
              }
              tooltipConfig={{
                placement: 'bottom-end'
              }}
            >
              <Icon color={'var(--color-error-default)'} name="tooltip" />
            </Tooltip>
          </S.ProductStockInfo>
        </S.ProductStockInfoOverlay>
      ) : (
        isSomeSkuAvailable && (
          <S.ProductStockInfoOverlay>
            <S.ProductStockInfo>
              <Tooltip content={t('unavailableSomeSkuTooltip')}>
                <Icon color={'var(--color-warning-default)'} name="tooltip" />
              </Tooltip>
            </S.ProductStockInfo>
          </S.ProductStockInfoOverlay>
        )
      )}
    </S.VerticalListItem>
  );
}

VerticalListItem.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string,
    meta: PropTypes.object,
    orderable: PropTypes.bool,
    brand: PropTypes.object,
    merchantProductId: PropTypes.string
  }),
  onClick: PropTypes.func,
  onRemoveItem: PropTypes.func,
  isSelected: PropTypes.bool,
  isAvailableSku: PropTypes.bool,
  isAllSkusAvailable: PropTypes.bool
};

VerticalListItem.S = S;
